import { Injectable, signal } from '@angular/core';

/**
 * Single source of truth for the light/dark UI theme.
 *
 * Toggling flips the global `.dark-mode` class on <body> (which drives Material's M3 tokens and the
 * custom CSS overrides in theme-utils.scss). Components that can't be themed by CSS alone — notably
 * the chart.js charts, whose ink (text/grid/axes/legend) is canvas-drawn — subscribe to `isDark` to
 * re-theme themselves.
 *
 * Persisted in localStorage so the choice carries across tabs (e.g. opening a page in a new tab)
 * and reloads. localStorage (not sessionStorage) is deliberate — sessionStorage is per-tab and
 * would not carry to a newly opened tab.
 */
const THEME_STORAGE_KEY = 'app-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  /**
   * Dark mode is switched off for now (September 2026): the toolbar toggle is hidden, and a stored
   * "dark" choice from before is ignored so nobody is left stuck in a theme they can't switch off.
   * Set to true to bring it back — the stored preference is untouched and applies again.
   */
  static readonly DARK_MODE_ENABLED = false;

  private readonly darkSignal = signal(this.readStoredPreference());

  /** Reactive dark-mode flag. */
  readonly isDark = this.darkSignal.asReadonly();

  constructor() {
    // Apply the stored preference to <body> as soon as the service is created.
    document.body.classList.toggle('dark-mode', this.darkSignal());
  }

  toggle(): void {
    this.setDark(!this.darkSignal());
  }

  setDark(dark: boolean): void {
    this.darkSignal.set(dark);
    document.body.classList.toggle('dark-mode', dark);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, dark ? 'dark' : 'light');
    } catch {
      // Ignore storage failures (private mode / quota) — the toggle still works for the session.
    }
  }

  private readStoredPreference(): boolean {
    if (!ThemeService.DARK_MODE_ENABLED) {
      return false;
    }
    try {
      return localStorage.getItem(THEME_STORAGE_KEY) === 'dark';
    } catch {
      return false;
    }
  }
}
